import React, { useState } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, List, ListItem, ListItemText } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssistantIcon from '@mui/icons-material/Assistant'; // Import Assistant icon
import AddIcon from '@mui/icons-material/Add'; // Import Add icon
import AccountCircleIcon from '@mui/icons-material/AccountCircle'; // Import AccountCircle icon
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png'; // Import the logo image

const FixedAppBar = styled(AppBar)`
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1100; /* Ensure it is above other content */
  background-color: white !important; /* Change background color to white and ensure it takes precedence */
  box-shadow: inset 0 4px 8px -2px rgba(65, 105, 225, 0.7), inset 0 10px 20px -5px rgba(65, 105, 225, 0.5); /* Darker light blue diminishing shadow */
`;

const Logo = styled.img`
  height: 40px; /* Adjust the height to fit the toolbar */
  margin-right: 16px; /* Add some space between the logo and the rest of the content */
`;

const NavButton = styled(Button)`
  text-transform: none !important;
  font-size: 1rem !important;
  color: black !important; /* Ensure text color is black for visibility */
  background-color: white !important; /* Set button background color to white */
  border-bottom: ${(props) => (props.selected ? '2px solid #1976d2' : 'none')} !important;
  border-radius: 0 !important;
  &:hover {
    color: #1EFFFE !important; /* Highlight font color on hover */
  }
`;

const Title = styled(Typography)`
  color: black !important; /* Change title color to black for better visibility */
  font-size: 1rem !important; /* Adjust font size if necessary */
`;

const StyledIconButton = styled(IconButton)`
  width: 48px;
  height: 48px;
  border-radius: 8px; /* Rounded corners */
  transition: all 0.3s ease; /* Smooth transition */
  &:hover {
    border: 1px solid #1976d2; /* Border color on hover */
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.12), inset 0 1px 2px rgba(0, 0, 0, 0.24); /* Emboss effect */
  }
`;

function NavBar() {
  const { username, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const notifications = [
    { id: 1, message: 'New task assigned to you: Design Homepage' },
    { id: 2, message: 'Task deadline approaching: Submit Report' },
    { id: 3, message: 'Milestone completed: Phase 1 Development' },
    { id: 4, message: 'Project status updated: Project Alpha is now Ongoing' },
  ];

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = () => {
    setNotificationOpen(true);
  };

  const handleNotificationClose = () => {
    setNotificationOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleNewProjectClick = () => {
    navigate('/project-edit/new'); // Navigate to the project creation form
  };

  return (
    <FixedAppBar position="fixed">
      <Toolbar>
        <Box display="flex" alignItems="center" flexGrow={1}>
          <Logo src={logo} alt="Logo" />
          <Title variant="h6">
            Project And Asset Management Platform
          </Title>
        </Box>
        <Box display="flex" justifyContent="flex-start" flexGrow={2}>
          <NavButton
            color="inherit"
            onClick={() => handleNavigation('/dashboard')}
            selected={location.pathname === '/dashboard'}
          >
            Dashboard
          </NavButton>
          <NavButton
            color="inherit"
            onClick={() => handleNavigation('/projects')}
            selected={location.pathname === '/projects'}
          >
            Projects
          </NavButton>
          <NavButton
            color="inherit"
            onClick={() => handleNavigation('/admin')}
            selected={location.pathname === '/admin'}
          >
            Admin
          </NavButton>
        </Box>
        <Box display="flex" alignItems="center" ml="auto">
          <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
            <Tooltip title="New Project">
              <StyledIconButton color="inherit" onClick={handleNewProjectClick}>
                <AddIcon sx={{ color: '#1976d2', fontSize: 28 }} /> {/* Adjusted size to fit the button */}
              </StyledIconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ color: '#1976d2' }}>New Project</Typography> {/* Match the color of the "CLOSE" button */}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
            <Tooltip title="AI Assistant">
              <StyledIconButton color="inherit">
                <AssistantIcon sx={{ color: '#1976d2', fontSize: 28 }} /> {/* Adjusted size to fit the button */}
              </StyledIconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ color: '#1976d2' }}>AI Assistant</Typography> {/* Match the color of the "CLOSE" button */}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
            <Tooltip title="You have new notifications">
              <StyledIconButton color="inherit" onClick={handleNotificationClick}>
                <NotificationsIcon sx={{ color: '#1976d2', fontSize: 28 }} /> {/* Adjusted size to fit the button */}
              </StyledIconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ color: '#1976d2' }}>Notifications</Typography> {/* Match the color of the "CLOSE" button */}
          </Box>
          <Box display="flex" flexDirection="column" alignItems="center" mr={2}>
            <Tooltip title={username}>
              <StyledIconButton onClick={handleMenuOpen} color="inherit">
                <AccountCircleIcon sx={{ color: '#1976d2', fontSize: 28 }} /> {/* Adjusted size to fit the button */}
              </StyledIconButton>
            </Tooltip>
            <Typography variant="caption" sx={{ color: '#1976d2' }}>{username}</Typography> {/* Display the logged-in username */}
          </Box>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>{username}</MenuItem>
            <MenuItem onClick={logout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <Dialog open={notificationOpen} onClose={handleNotificationClose}>
        <DialogTitle>Notifications</DialogTitle>
        <DialogContent>
          <List>
            {notifications.map((notification) => (
              <ListItem key={notification.id}>
                <ListItemText primary={notification.message} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNotificationClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </FixedAppBar>
  );
}

export default NavBar;